
import React from "react";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import PageLayout from "@/components/layout/PageLayout";
import SEO from "@/components/SEO";

const About: React.FC = () => {
  return (
    <PageLayout title="About Remote Viewing">
      <SEO
        title="About Remote Viewing — RemoteViz.ai"
        description="Learn what remote viewing is, its history from the Stargate Project, and how RemoteViz uses AI to support modern practice."
        path="/about"
        type="article"
      />
      <div className="max-w-4xl mx-auto py-12 px-6">
        <h1 className="text-4xl mb-8">About Remote Viewing</h1>
        
        <Card className="mb-8 border-0 shadow-none bg-background">
          <CardContent className="pt-6 px-0">
            <h2 className="text-2xl font-semibold mb-4">What is Remote Viewing?</h2>
            <p className="text-foreground mb-4">
              Remote viewing is the practice of attempting to perceive or describe details about a distant or unseen target using extrasensory perception (ESP) or "anomalous cognition." This intriguing concept encompasses the belief that individuals can access information beyond the physical senses, allowing them to "see" locations, objects, or events that are not within their immediate vicinity.
            </p>
          </CardContent>
        </Card>

        <Accordion type="single" collapsible className="mb-8 px-4">
          <AccordionItem value="historical-background">
            <AccordionTrigger className="text-xl font-medium">Historical Background</AccordionTrigger>
            <AccordionContent className="text-foreground">
              <p>
                The term "remote viewing" emerged during the Cold War era, when intelligence agencies sought to explore the potential of psychic phenomena for espionage purposes. Programs such as the U.S. government's Stargate Project aimed to investigate how individuals could tap into their innate psychic abilities to gather intelligence information. Over the years, remote viewing has transitioned from government-sponsored programs to becoming a widely researched and practiced phenomenon in various fields.
              </p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="how-it-works">
            <AccordionTrigger className="text-xl font-medium">How Does It Work?</AccordionTrigger>
            <AccordionContent className="text-foreground">
              <p className="mb-4">
                Remote viewing involves a viewer attempting to gather information about a specified target. This target could be a geographical location, an object, or even a person. During a remote viewing session, participants typically receive a unique target code, which allows them to focus their thoughts and impressions on the specified target without any prior knowledge.
              </p>
              <p>
                Practitioners of remote viewing often go through structured protocols that help enhance their intuitive skills. These protocols may include relaxation techniques, mental exercises, and guided visualizations aimed at opening the mind to receive information.
              </p>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="app-purpose">
            <AccordionTrigger className="text-xl font-medium">The Purpose of This App</AccordionTrigger>
            <AccordionContent className="text-foreground">
              <p className="mb-4">
                This remote viewing app is designed to facilitate your exploration of this fascinating ability. We provide a straightforward and user-friendly platform for users to engage in remote viewing experiments. Here's what you can expect:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Unique Targets: Each session features a distinct target, either a Google Street View location or a random image, accompanied by a unique target code.</li>
                <li>Guided Sessions: Users can submit notes, impressions, or sketches based on their perceptions of the target, fostering a deeper engagement with their intuitive process.</li>
                <li>Target Integrity: Once generated, the targets remain unchanged throughout the session, ensuring a consistent and reliable experience for tracking your remote viewing efforts.</li>
                <li>Feedback and Analysis: After revealing the target, users have the opportunity to explore feedback on their submissions, helping to refine and enhance their skills.</li>
              </ul>
            </AccordionContent>
          </AccordionItem>
          
          <AccordionItem value="who-can-participate">
            <AccordionTrigger className="text-xl font-medium">Who Can Participate?</AccordionTrigger>
            <AccordionContent className="text-foreground">
              <p>
                Anyone with an interest in exploring the potential of their intuition can participate in remote viewing sessions. Whether you are a seasoned practitioner or a newcomer, this app offers a supportive environment to develop your abilities and explore the depths of your inner perception.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        
        <Card className="border-0 shadow-none bg-background">
          <CardContent className="pt-6 px-0">
            <h2 className="text-2xl font-semibold mb-4">Join Us on This Journey</h2>
            <p className="text-foreground mb-4">
              We invite you to embark on a journey of discovery through remote viewing. Engage your mind, develop your intuitive abilities, and connect with the unseen world around you. Experience the excitement of perceiving beyond physical boundaries and uncovering hidden information through this age-old practice.
            </p>
            <p className="text-primary font-medium italic">
              Unleash the potential of your mind—one view at a time.
            </p>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
};

export default About;
